import { useContext, useEffect } from 'react';
import { ThemeContext } from '@/contexts/ThemeContext';
import { Toaster, toast } from '@matrix-forge/ui/components/sonner';

type StudioToastLevel = 'success' | 'error' | 'info' | 'warning';

type StudioToastPayload = {
  level: StudioToastLevel;
  title: string;
  description?: string | null;
};

type StudioToastMessage = {
  type: 'matrix:studio:toast';
  payload: StudioToastPayload;
};

const isStudioToastMessage = (data: unknown): data is StudioToastMessage => {
  if (!data || typeof data !== 'object') return false;
  const candidate = data as { type?: unknown; payload?: unknown };
  if (candidate.type !== 'matrix:studio:toast') return false;
  const payload = candidate.payload as { level?: unknown; title?: unknown } | undefined;
  if (!payload || typeof payload !== 'object') return false;
  if (typeof payload.title !== 'string') return false;
  return ['success', 'error', 'info', 'warning'].includes(payload.level as string);
};

export const AppToaster = () => {
  const { theme } = useContext(ThemeContext);

  // Bridge for the embedded Organization LiveView. The iframe forwards its
  // `phx:studio_toast` events here via postMessage; we map them onto the
  // existing Sonner toast layer so server-side actions feel native to the
  // studio shell. Same-origin guard prevents arbitrary cross-frame messages
  // from triggering toasts.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!isStudioToastMessage(event.data)) return;

      const { level, title, description } = event.data.payload;
      const options = description ? { description } : undefined;

      switch (level) {
        case 'success':
          toast.success(title, options);
          break;
        case 'error':
          toast.error(title, options);
          break;
        case 'warning':
          toast.warning(title, options);
          break;
        case 'info':
        default:
          toast.info(title, options);
          break;
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return <Toaster theme={theme as 'light' | 'dark' | undefined} />;
};
